import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  type Timestamp,
} from 'firebase/firestore';
import { formatDistanceToNow } from 'date-fns';
import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  KeyRound,
  Loader2,
  Pencil,
  RefreshCw,
  ShieldOff,
  X,
} from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../../utils/AuthContext';
import { crmAuthedFetch } from '../../../utils/crmAuthedFetch';
import { db } from '../../../utils/firebase';
import SettingsSectionShell from '../SettingsSectionShell';
import './AgentApiKeysSettingsSection.css';

const GATEWAY = 'agentGatewayApi';

type ScopeCatalogItem = { id: string; label: string; financial: boolean };
type AgentCatalogItem = { id: string; label: string };

type KeyCandidate = {
  uid: string;
  displayName: string;
  email: string | null;
  role: string;
  designations: string[];
  orgDepartment: string | null;
  isAgentBot: boolean;
  maxScopes: string[];
};

type KeyReach = {
  scopes: string[];
  allowedAgents: string[];
  canReachFinance: boolean;
  canReachAdminExec: boolean;
  surfaces: string[];
  notes: string[];
};

type EmployeeKeyRow = {
  keyId: string;
  uid: string | null;
  keyPrefix: string | null;
  label: string | null;
  scopes: string[];
  allowedAgents: string[];
  roleAtIssue: string | null;
  designationsAtIssue: string[];
  isServiceBot: boolean;
  revokedAt: string | null;
  createdAt: string | null;
  lastUsedAt: string | null;
  requestCountTotal: number;
  requestCountDenied: number;
  reach: KeyReach;
};

type AuditEvent = {
  id: string;
  ts?: Timestamp | null;
  action?: string;
  agentId?: string | null;
  decision?: 'allow' | 'deny';
  reason?: string | null;
  keyId?: string | null;
  keyPrefix?: string | null;
  keyKind?: string | null;
  uid?: string | null;
  sourceIp?: string | null;
  userAgent?: string | null;
  endpoint?: string | null;
};

async function gatewayAction<T = Record<string, unknown>>(
  action: string,
  body: Record<string, unknown> = {},
): Promise<T> {
  const res = await crmAuthedFetch(GATEWAY, {
    method: 'POST',
    body: JSON.stringify({ action, ...body }),
  });
  const data = (await res.json()) as T & { success?: boolean; error?: string };
  if (!res.ok || data.success === false) {
    throw new Error(data.error || `HTTP ${res.status}`);
  }
  return data;
}

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

function startOfUtcDay(): Date {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

const AgentApiKeysSettingsSection: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [catalogScopes, setCatalogScopes] = useState<ScopeCatalogItem[]>([]);
  const [catalogAgents, setCatalogAgents] = useState<AgentCatalogItem[]>([]);
  const [catalogNotes, setCatalogNotes] = useState<string[]>([]);
  const [candidates, setCandidates] = useState<KeyCandidate[]>([]);
  const [keys, setKeys] = useState<EmployeeKeyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [selectedKeyId, setSelectedKeyId] = useState<string | null>(null);
  const [showIssue, setShowIssue] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [targetUid, setTargetUid] = useState('');
  const [label, setLabel] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>(['search.general', 'chat.reply']);
  const [selectedAgents, setSelectedAgents] = useState<string[]>([]);
  const [preview, setPreview] = useState<{ maxScopes: string[]; reach: KeyReach } | null>(null);
  const [issuedRawKey, setIssuedRawKey] = useState<string | null>(null);
  const [issuedNote, setIssuedNote] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [editNotice, setEditNotice] = useState<string | null>(null);

  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [auditError, setAuditError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [catalog, list, people] = await Promise.all([
        gatewayAction<{
          scopes: ScopeCatalogItem[];
          agents: AgentCatalogItem[];
          notes: string[];
        }>('employeeKeyCatalog'),
        gatewayAction<{ keys: EmployeeKeyRow[] }>('listEmployeeAgentKeys'),
        gatewayAction<{ users: KeyCandidate[] }>('listEmployeeKeyCandidates'),
      ]);
      setCatalogScopes(catalog.scopes || []);
      setCatalogAgents(catalog.agents || []);
      setCatalogNotes(catalog.notes || []);
      setKeys(list.keys || []);
      setCandidates(people.users || []);
      setSelectedKeyId(prev => {
        if (prev && list.keys?.some(k => k.keyId === prev)) return prev;
        if (list.keys?.length) {
          return list.keys.find(k => !k.revokedAt)?.keyId || list.keys[0].keyId;
        }
        return null;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    void load();
  }, [isAdmin, load]);

  // Live audit feed (admin-only rules). Single-field order avoids waiting on composite indexes.
  useEffect(() => {
    if (!isAdmin) return;
    const q = query(collection(db, 'agentAccessAudit'), orderBy('ts', 'desc'), limit(120));
    const unsub = onSnapshot(
      q,
      snap => {
        const rows = snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<AuditEvent, 'id'>) }));
        setAuditEvents(
          rows.filter(e => e.keyKind === 'employee' || e.keyKind === 'mention' || e.keyKind === 'staff'),
        );
        setAuditError(null);
      },
      err => {
        console.error('[AgentApiKeys] audit stream', err);
        setAuditError(err.message || 'Could not subscribe to audit feed');
      },
    );
    return () => unsub();
  }, [isAdmin]);

  const selectedKey = useMemo(
    () => keys.find(k => k.keyId === selectedKeyId) || null,
    [keys, selectedKeyId],
  );

  const target = useMemo(
    () => candidates.find(c => c.uid === targetUid) || null,
    [candidates, targetUid],
  );

  const keyAudit = useMemo(() => {
    if (!selectedKey) return [];
    return auditEvents.filter(e => e.keyId === selectedKey.keyId);
  }, [auditEvents, selectedKey]);

  const keySummary = useMemo(() => {
    if (!selectedKey) return null;
    const dayStart = startOfUtcDay().getTime();
    const today = keyAudit.filter(e => {
      const ms = e.ts?.toMillis?.() || 0;
      return ms >= dayStart;
    });
    const endpoints = new Set(
      today.map(e => e.endpoint || e.agentId || e.action || '').filter(Boolean),
    );
    const success = today.filter(e => e.decision === 'allow').length;
    const denied = today.filter(e => e.decision === 'deny').length;
    const last = keyAudit[0];
    return {
      lastUsed: selectedKey.lastUsedAt,
      todayCount: today.length,
      success,
      denied,
      endpoints: Array.from(endpoints),
      lastIp: last?.sourceIp || null,
      lastUa: last?.userAgent || null,
      total: selectedKey.requestCountTotal,
      totalDenied: selectedKey.requestCountDenied,
    };
  }, [keyAudit, selectedKey]);

  const previewUid = showEdit ? selectedKey?.uid || '' : targetUid;
  const previewOpen = (showIssue && Boolean(targetUid)) || (showEdit && Boolean(previewUid));

  useEffect(() => {
    if (!previewOpen || !previewUid) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    const handle = window.setTimeout(() => {
      void (async () => {
        try {
          const data = await gatewayAction<{
            maxScopes: string[];
            reach: KeyReach;
            scopes: string[];
          }>('previewEmployeeAgentKey', {
            targetUid: previewUid,
            scopes: selectedScopes,
            allowedAgents: selectedAgents,
          });
          if (cancelled) return;
          setPreview({ maxScopes: data.maxScopes, reach: data.reach });
        } catch {
          if (!cancelled) setPreview(null);
        }
      })();
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [previewOpen, previewUid, selectedScopes, selectedAgents]);

  const toggleScope = (id: string) => {
    setSelectedScopes(prev => (prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]));
  };

  const toggleAgent = (id: string) => {
    setSelectedAgents(prev => (prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]));
  };

  const issueKey = async () => {
    if (!targetUid) return;
    setBusy(true);
    setError(null);
    try {
      const data = await gatewayAction<{
        rawKey: string;
        keyId: string;
        note?: string;
        reach: KeyReach;
      }>('issueEmployeeAgentKey', {
        targetUid,
        scopes: selectedScopes,
        allowedAgents: selectedAgents,
        label: label.trim() || undefined,
      });
      setIssuedRawKey(data.rawKey);
      setIssuedNote(data.note || null);
      setShowIssue(false);
      await load();
      setSelectedKeyId(data.keyId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Issue failed');
    } finally {
      setBusy(false);
    }
  };

  const revokeKey = async (keyId: string) => {
    if (!window.confirm('Revoke this key? Callers using it will be denied immediately.')) return;
    setBusy(true);
    try {
      await gatewayAction('revokeEmployeeAgentKey', { keyId });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Revoke failed');
    } finally {
      setBusy(false);
    }
  };

  const openEditPermissions = (key: EmployeeKeyRow) => {
    setShowIssue(false);
    setIssuedRawKey(null);
    setEditNotice(null);
    setLabel(key.label || '');
    setSelectedScopes(key.scopes.length ? [...key.scopes] : ['search.general', 'chat.reply']);
    setSelectedAgents([...key.allowedAgents]);
    setShowEdit(true);
  };

  const savePermissions = async () => {
    if (!selectedKey) return;
    setBusy(true);
    setError(null);
    try {
      const data = await gatewayAction<{
        note?: string;
        previousScopes: string[];
        scopes: string[];
      }>('updateEmployeeAgentKey', {
        keyId: selectedKey.keyId,
        scopes: selectedScopes,
        allowedAgents: selectedAgents,
        label: label.trim() || null,
      });
      setShowEdit(false);
      setEditNotice(
        data.note ||
          `Updated scopes: ${(data.previousScopes || []).join(', ') || '(none)'} → ${(data.scopes || []).join(', ') || '(none)'}`,
      );
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  if (!isAdmin) {
    return <Navigate to="/settings/account" replace />;
  }

  return (
    <SettingsSectionShell
      title="Agent API Keys"
      description="Admin-only. Issue scoped employee and service-bot keys (hash-only at rest). Master gateway key stays valid until you rotate it."
    >
      <div className="ak-page">
        {error && (
          <p className="ak-banner ak-banner--err" role="alert">
            <AlertTriangle size={14} aria-hidden /> {error}
          </p>
        )}

        {issuedRawKey && (
          <div className="ak-once" role="status">
            <div className="ak-once-head">
              <CheckCircle2 size={16} aria-hidden />
              <strong>Copy this key now — it will not be shown again</strong>
              <button type="button" className="ak-icon-btn" onClick={() => setIssuedRawKey(null)} aria-label="Dismiss">
                <X size={14} />
              </button>
            </div>
            <code className="ak-once-key">{issuedRawKey}</code>
            <div className="ak-once-actions">
              <button
                type="button"
                className="pm-btn pm-btn-primary pm-btn-sm"
                onClick={() => {
                  void navigator.clipboard.writeText(issuedRawKey).then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                  });
                }}
              >
                <Copy size={14} aria-hidden /> {copied ? 'Copied' : 'Copy key'}
              </button>
              <span className="ak-muted">{issuedNote}</span>
            </div>
          </div>
        )}

        {editNotice && (
          <div className="ak-once" role="status">
            <div className="ak-once-head">
              <CheckCircle2 size={16} aria-hidden />
              <strong>Permissions updated</strong>
              <button type="button" className="ak-icon-btn" onClick={() => setEditNotice(null)} aria-label="Dismiss">
                <X size={14} />
              </button>
            </div>
            <span className="ak-muted">{editNotice}</span>
          </div>
        )}

        <div className="ak-toolbar">
          <button
            type="button"
            className="pm-btn pm-btn-primary pm-btn-sm"
            onClick={() => {
              setShowIssue(true);
              setIssuedRawKey(null);
              setLabel('');
              setSelectedAgents([]);
              setSelectedScopes(['search.general', 'chat.reply']);
            }}
            disabled={busy}
          >
            <KeyRound size={14} aria-hidden /> Issue key
          </button>
          <button type="button" className="pm-btn pm-btn-secondary pm-btn-sm" onClick={() => void load()} disabled={loading || busy}>
            <RefreshCw size={14} aria-hidden /> Refresh
          </button>
        </div>

        {catalogNotes.length > 0 && (
          <ul className="ak-notes">
            {catalogNotes.map(n => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        )}

        {loading ? (
          <p className="ak-muted">
            <Loader2 size={14} className="settings-spin" aria-hidden /> Loading keys…
          </p>
        ) : (
          <div className="ak-split">
            <div className="ak-list-panel">
              <h3 className="ak-h">Keys</h3>
              {keys.length === 0 ? (
                <p className="ak-empty">No employee / service-bot keys yet.</p>
              ) : (
                <ul className="ak-key-list">
                  {keys.map(k => {
                    const person = candidates.find(c => c.uid === k.uid);
                    return (
                      <li key={k.keyId}>
                        <button
                          type="button"
                          className={`ak-key-row ${selectedKeyId === k.keyId ? 'is-active' : ''} ${k.revokedAt ? 'is-revoked' : ''}`}
                          onClick={() => setSelectedKeyId(k.keyId)}
                        >
                          <span className="ak-key-title">
                            {k.label || person?.displayName || k.keyPrefix || k.keyId.slice(0, 8)}
                            {k.isServiceBot ? <span className="ak-chip">bot</span> : null}
                            {k.revokedAt ? <span className="ak-chip ak-chip--danger">revoked</span> : null}
                          </span>
                          <span className="ak-key-meta">
                            {k.keyPrefix}… · {k.scopes.length} scopes · used {formatWhen(k.lastUsedAt)}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="ak-detail-panel">
              {!selectedKey ? (
                <p className="ak-empty">Select a key to see reach and live activity.</p>
              ) : (
                <>
                  <div className="ak-detail-head">
                    <div>
                      <h3 className="ak-h">{selectedKey.label || selectedKey.keyPrefix}</h3>
                      <p className="ak-muted">
                        {candidates.find(c => c.uid === selectedKey.uid)?.displayName || selectedKey.uid}
                        {' · '}
                        role {selectedKey.roleAtIssue || '—'}
                        {selectedKey.designationsAtIssue?.length
                          ? ` · ${selectedKey.designationsAtIssue.join(', ')}`
                          : ''}
                      </p>
                    </div>
                    {!selectedKey.revokedAt && (
                      <div className="ak-detail-actions">
                        <button
                          type="button"
                          className="pm-btn pm-btn-secondary pm-btn-sm"
                          onClick={() => openEditPermissions(selectedKey)}
                          disabled={busy}
                        >
                          <Pencil size={14} aria-hidden /> Edit permissions
                        </button>
                        <button
                          type="button"
                          className="pm-btn pm-btn-secondary pm-btn-sm ak-revoke"
                          onClick={() => void revokeKey(selectedKey.keyId)}
                          disabled={busy}
                        >
                          <ShieldOff size={14} aria-hidden /> Revoke
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="ak-stats">
                    <div>
                      <span className="ak-stat-label">Last used</span>
                      <strong>{formatWhen(keySummary?.lastUsed)}</strong>
                    </div>
                    <div>
                      <span className="ak-stat-label">Today</span>
                      <strong>{keySummary?.todayCount ?? 0}</strong>
                    </div>
                    <div>
                      <span className="ak-stat-label">OK / denied today</span>
                      <strong>
                        {keySummary?.success ?? 0} / {keySummary?.denied ?? 0}
                      </strong>
                    </div>
                    <div>
                      <span className="ak-stat-label">Lifetime</span>
                      <strong>
                        {keySummary?.total ?? 0} ({keySummary?.totalDenied ?? 0} denied)
                      </strong>
                    </div>
                  </div>

                  <h4 className="ak-subh">What this key can reach</h4>
                  <div className="ak-tags">
                    {selectedKey.scopes.map(s => (
                      <span key={s} className="ak-tag">{s}</span>
                    ))}
                  </div>
                  <div className="ak-tags">
                    {(selectedKey.allowedAgents.length
                      ? selectedKey.allowedAgents
                      : selectedKey.reach?.surfaces || []
                    ).map(s => (
                      <span key={s} className="ak-tag ak-tag--agent">{s}</span>
                    ))}
                  </div>
                  <ul className="ak-notes">
                    {(selectedKey.reach?.notes || []).map(n => (
                      <li key={n}>{n}</li>
                    ))}
                    {keySummary?.endpoints?.length ? (
                      <li>Endpoints today: {keySummary.endpoints.join(', ')}</li>
                    ) : null}
                    {keySummary?.lastIp ? <li>Last IP: {keySummary.lastIp}</li> : null}
                    {keySummary?.lastUa ? <li>Last UA: {keySummary.lastUa}</li> : null}
                  </ul>

                  <h4 className="ak-subh">Live activity</h4>
                  {auditError && <p className="ak-banner ak-banner--err">{auditError}</p>}
                  {keyAudit.length === 0 ? (
                    <p className="ak-empty">No audit events for this key yet.</p>
                  ) : (
                    <ul className="ak-feed">
                      {keyAudit.slice(0, 40).map(ev => (
                        <li key={ev.id} className={ev.decision === 'deny' ? 'is-deny' : 'is-allow'}>
                          <span className="ak-feed-dec">{ev.decision}</span>
                          <span className="ak-feed-main">
                            {ev.agentId || 'gateway'}.{ev.action}
                            {ev.reason ? ` — ${ev.reason}` : ''}
                          </span>
                          <span className="ak-feed-meta">
                            {ev.ts?.toDate
                              ? formatDistanceToNow(ev.ts.toDate(), { addSuffix: true })
                              : ''}
                            {ev.sourceIp ? ` · ${ev.sourceIp}` : ''}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        <div className="ak-feed-global">
          <h3 className="ak-h">All scoped-key activity</h3>
          {auditEvents.length === 0 ? (
            <p className="ak-empty">Waiting for live events…</p>
          ) : (
            <ul className="ak-feed">
              {auditEvents.slice(0, 25).map(ev => (
                <li key={ev.id} className={ev.decision === 'deny' ? 'is-deny' : 'is-allow'}>
                  <span className="ak-feed-dec">{ev.decision}</span>
                  <span className="ak-feed-main">
                    {(ev.keyPrefix || ev.keyId || 'key').toString().slice(0, 16)} · {ev.agentId || 'gateway'}.{ev.action}
                  </span>
                  <span className="ak-feed-meta">
                    {ev.ts?.toDate ? formatDistanceToNow(ev.ts.toDate(), { addSuffix: true }) : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {showIssue && (
          <div className="ak-modal-backdrop" role="presentation" onClick={() => setShowIssue(false)}>
            <div
              className="ak-modal"
              role="dialog"
              aria-labelledby="ak-issue-title"
              onClick={e => e.stopPropagation()}
            >
              <div className="ak-modal-head">
                <h3 id="ak-issue-title">Issue scoped API key</h3>
                <button type="button" className="ak-icon-btn" onClick={() => setShowIssue(false)} aria-label="Close">
                  <X size={16} />
                </button>
              </div>

              <label className="ak-field">
                <span>Employee or service bot</span>
                <select
                  value={targetUid}
                  onChange={e => setTargetUid(e.target.value)}
                  className="ops-modal-select"
                >
                  <option value="">Select…</option>
                  {candidates.map(c => (
                    <option key={c.uid} value={c.uid}>
                      {c.displayName}
                      {c.isAgentBot ? ' (bot)' : ''} — {c.role}
                      {c.orgDepartment ? ` / ${c.orgDepartment}` : ''}
                    </option>
                  ))}
                </select>
              </label>

              <label className="ak-field">
                <span>Label (optional)</span>
                <input
                  className="ops-modal-input"
                  value={label}
                  onChange={e => setLabel(e.target.value)}
                  placeholder="e.g. Caesar search read-only"
                />
              </label>

              {target && (
                <>
                  <p className="ak-muted">
                    Max issuable scopes for this user (financial / exec stripped for non-admin & bots):
                  </p>
                  <div className="ak-check-grid">
                    {(preview?.maxScopes || target.maxScopes).map(id => {
                      const meta = catalogScopes.find(s => s.id === id);
                      const disabled = meta?.financial && target.role !== 'admin';
                      return (
                        <label key={id} className={`ak-check ${disabled ? 'is-disabled' : ''}`}>
                          <input
                            type="checkbox"
                            checked={selectedScopes.includes(id)}
                            disabled={Boolean(disabled)}
                            onChange={() => toggleScope(id)}
                          />
                          {id}
                        </label>
                      );
                    })}
                  </div>

                  <p className="ak-muted">Allowed API surfaces (least privilege — pick only what is needed)</p>
                  <div className="ak-check-grid">
                    {catalogAgents.map(a => (
                      <label key={a.id} className="ak-check">
                        <input
                          type="checkbox"
                          checked={selectedAgents.includes(a.id)}
                          onChange={() => toggleAgent(a.id)}
                        />
                        {a.label}
                      </label>
                    ))}
                  </div>

                  {preview?.reach && (
                    <div className="ak-preview">
                      <strong>Reach preview</strong>
                      <ul className="ak-notes">
                        {preview.reach.notes.map(n => (
                          <li key={n}>{n}</li>
                        ))}
                        <li>Surfaces: {preview.reach.surfaces.join(', ') || 'none'}</li>
                        <li>
                          Finance / exec data:{' '}
                          {preview.reach.canReachFinance ? 'YES (admin key)' : 'blocked'}
                        </li>
                      </ul>
                    </div>
                  )}
                </>
              )}

              <div className="ak-modal-actions">
                <button type="button" className="pm-btn pm-btn-secondary pm-btn-sm" onClick={() => setShowIssue(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="pm-btn pm-btn-primary pm-btn-sm"
                  disabled={!targetUid || busy || selectedScopes.length === 0}
                  onClick={() => void issueKey()}
                >
                  {busy ? 'Issuing…' : 'Issue key'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showEdit && selectedKey && (
          <div className="ak-modal-backdrop" role="presentation" onClick={() => setShowEdit(false)}>
            <div
              className="ak-modal"
              role="dialog"
              aria-labelledby="ak-edit-title"
              onClick={e => e.stopPropagation()}
            >
              <div className="ak-modal-head">
                <h3 id="ak-edit-title">Edit permissions</h3>
                <button type="button" className="ak-icon-btn" onClick={() => setShowEdit(false)} aria-label="Close">
                  <X size={16} />
                </button>
              </div>

              <p className="ak-muted">
                Changes scopes and allowed agents on the live key without reissuing. The secret stays the
                same and keys never expire.
              </p>
              <p className="ak-muted">
                Key {selectedKey.keyPrefix}… ·{' '}
                {candidates.find(c => c.uid === selectedKey.uid)?.displayName || selectedKey.uid}
              </p>

              <label className="ak-field">
                <span>Label (optional)</span>
                <input
                  className="ops-modal-input"
                  value={label}
                  onChange={e => setLabel(e.target.value)}
                  placeholder="e.g. Caesar search read-only"
                />
              </label>

              <p className="ak-muted">
                Max issuable scopes for this user (financial / exec stripped for non-admin & bots):
              </p>
              <div className="ak-check-grid">
                {(preview?.maxScopes ||
                  candidates.find(c => c.uid === selectedKey.uid)?.maxScopes ||
                  selectedKey.scopes
                ).map(id => {
                  const person = candidates.find(c => c.uid === selectedKey.uid);
                  const meta = catalogScopes.find(s => s.id === id);
                  const disabled = Boolean(meta?.financial && person?.role !== 'admin');
                  return (
                    <label key={id} className={`ak-check ${disabled ? 'is-disabled' : ''}`}>
                      <input
                        type="checkbox"
                        checked={selectedScopes.includes(id)}
                        disabled={disabled}
                        onChange={() => toggleScope(id)}
                      />
                      {id}
                    </label>
                  );
                })}
              </div>

              <p className="ak-muted">Allowed API surfaces (least privilege — pick only what is needed)</p>
              <div className="ak-check-grid">
                {catalogAgents.map(a => (
                  <label key={a.id} className="ak-check">
                    <input
                      type="checkbox"
                      checked={selectedAgents.includes(a.id)}
                      onChange={() => toggleAgent(a.id)}
                    />
                    {a.label}
                  </label>
                ))}
              </div>

              {preview?.reach && (
                <div className="ak-preview">
                  <strong>Reach preview</strong>
                  <ul className="ak-notes">
                    {preview.reach.notes.map(n => (
                      <li key={n}>{n}</li>
                    ))}
                    <li>Surfaces: {preview.reach.surfaces.join(', ') || 'none'}</li>
                    <li>
                      Finance / exec data:{' '}
                      {preview.reach.canReachFinance ? 'YES (admin key)' : 'blocked'}
                    </li>
                  </ul>
                </div>
              )}

              <div className="ak-modal-actions">
                <button type="button" className="pm-btn pm-btn-secondary pm-btn-sm" onClick={() => setShowEdit(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="pm-btn pm-btn-primary pm-btn-sm"
                  disabled={busy || selectedScopes.length === 0}
                  onClick={() => void savePermissions()}
                >
                  {busy ? 'Saving…' : 'Save permissions'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </SettingsSectionShell>
  );
};

export default AgentApiKeysSettingsSection;
